<template>
	<FormContainer
		class="form"
		title="User Info"
		:as-dialog
		:visible
		@close="emit('close')">
		<form @submit.prevent="submit">
			<div>
				<span class="form-token-counter">{{ tokensOwned }} tokens</span>
			</div>
			<FormInput
				label="Email"
				:error-message="formErrors.email">
				<InputText v-model="formData.email" />
			</FormInput>
			<div class="form__footer-container">
				<Button
					class="form__change-password-button"
					icon="fal fa-key"
					label="Change Password"
					severity="secondary"
					:loading="loading"
					@click="changePassword"
					@submit.stop />
				<Button
					class="form__logout-button"
					icon="fal fa-save"
					label="Update"
					type="submit"
					:loading="loading" />
			</div>
		</form>
	</FormContainer>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import UsersApi from '@/models/User/Api'
import type { User } from '@/models/User/Model'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { useForm } from '@/helpers/form'
import FormInput from '@/components/FormInput.vue'
import FormContainer from '@/components/FormContainer.vue'
import { useAuthStore } from '@/stores/Auth'
import { useToast } from 'primevue/usetoast'
import { useUserDetailsState } from '@/models/User/States'

type FormData = {
	email: string
}
const auth = useAuthStore()

const emit = defineEmits<{
	(e: 'start-loading'): void
	(e: 'stop-loading'): void
	(e: 'close'): void
	(e: 'submit'): void
	(e: 'created', entity: User | undefined): void
	(e: 'updated'): void
	(e: 'deleted'): void
}>()

const props = withDefaults(
	defineProps<{
		id?: User['id']
		hideInputs?: (keyof FormData)[]
		defaultValues?: Partial<FormData>
		forceValues?: Partial<FormData>
		shouldRedirect?: boolean
		attachTo?: Record<string, { method: 'associate' | 'syncWithoutDetaching'; id: string | number }>
		asDialog?: boolean
		visible?: boolean
		hideRemove?: boolean
	}>(),
	{
		id: undefined,
		hideInputs: () => [],
		defaultValues: () => ({}),
		forceValues: () => ({}),
		shouldRedirect: true,
		attachTo: undefined,
		asDialog: false,
		visible: false,
		hideRemove: false,
	},
)

const authStore = useAuthStore()
const toast = useToast()
const detailsState = useUserDetailsState()

const user = computed(() => (isOwnAccount.value ? authStore.user : detailsState.details.value))
const isOwnAccount = computed(() => !props.id || props.id === auth.user?.id)
const tokensOwned = computed(() => {
	if (isOwnAccount.value) return auth.user?.tokens_remaining ?? 0
	return user.value?.tokens_remaining ?? 0
})

const { formData, loading, formErrors, reset, submit } = useForm({
	api: () => new UsersApi(),
	defaultData: () => ({
		email: '',
	}),
	forceValues: () => props.forceValues,
	attachTo: () => props.attachTo,
	id: toRef(props, 'id'),
	onStartLoading: () => emit('start-loading'),
	onStopLoading: () => emit('stop-loading'),
	onSubmit: () => emit('submit'),
	onUpdated: () => {
		emit('updated')
		if (isOwnAccount.value) authStore.getUser()
	},
	onDeleted: () => emit('deleted'),
})

watch(
	() => props.id,
	async (id) => {
		if (id && !isOwnAccount.value) {
			await detailsState.getDetails(id)
		}
	},
	{ immediate: true },
)

watch(
	() => props.visible,
	(val) => {
		if (val) reset()
	},
)

async function changePassword() {
	if (!user.value) {
		throw new Error('User is not set. This should never happen')
	}
	loading.value = true
	try {
		await authStore.requestPasswordReset({ email: user.value.email })
		toast.add({
			severity: 'info',
			summary: 'Password reset requested',
			detail: `An email with password reset instructions has been sent to ${user.value.email}`,
			life: 5000,
		})
	} finally {
		loading.value = false
	}
}
</script>

<style scoped lang="scss">
.form {
	max-width: unset;

	form {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;

		& > * {
			width: 100%;
		}

		.form__footer-container {
			display: flex;
			align-items: center;
			gap: 10px;

			.form__change-password-button,
			.form__logout-button {
				width: 100%;
			}
		}

		&--edit {
			.form__footer-container {
				justify-content: space-between;
			}
		}
	}
	.form-token-counter {
		background: rgba(238, 255, 1, 0.08);
		border: 1px solid rgba(238, 255, 1, 0.3);
		color: var(--p-yellow-400);
		padding: 6px 10px;
		border-radius: 999px;
		font-size: 12px;
		line-height: 1;
	}
}
</style>
